/**
 * Listing Card Component
 *
 * Dedicated component for 'listing' resource items (restaurants, attractions, shops, etc.).
 * Supports both feed card and map popup rendering via the `variant` prop.
 * Renders the listing schema fields: image, tag, title, full address, time suggestion,
 * rating, operating hours, phone, email, description accordion, amenities and action links.
 */

import { h } from 'preact';
import htm from 'htm';
import { IconMapPin, IconCalendar } from '../../../components/Icons.js';
import { CardMedia } from './shared/CardMedia.js';
import { CardDescription } from './shared/CardDescription.js';
import { CardLinks } from './shared/CardLinks.js';
import { CardChips } from './shared/CardChips.js';
import { asText, pickText, pickList, label, buildFullAddress } from './shared/fields.js';

const html = htm.bind(h);

/**
 * Renders a listing item as a result card or map popup.
 *
 * @param {Object} props
 * @param {Object} props.item - The listing item data.
 * @param {Object} [props.block] - Parent block data.
 * @param {Object} props.ctx - View context with `t`, `config`, and `onShowPinOnMap`.
 * @param {string} [props.variant='card'] - 'card' for blocks feed or 'popup' for map infowindow.
 * @param {Function} [props.onClose] - Callback when the popup close button is clicked.
 */
export function ListingCard({ item, block, ctx, variant = 'card', onClose }) {
  const prof = item?.profile || {};
  const isPopup = variant === 'popup';

  const tag = pickText(item, 'categories', 'category', 'tag') || label(ctx, 'type_listing', 'Listing');
  const name = pickText(item, 'name', 'label');
  const address = buildFullAddress(item);
  const timeSuggestion = asText(item?.time_suggestion) || asText(prof.time_suggestion);
  const ratingScore = pickText(item, 'rating_score', 'rating');
  const reviewCount = pickText(item, 'review_count');
  const operatingHours = pickText(item, 'operating_hours');
  const phone = pickText(item, 'phone');
  const email = pickText(item, 'email');
  const description = pickText(item, 'description');
  const amenities = pickList(item, 'amenities');
  const website = pickText(item, 'website');
  const bookingUrl = pickText(item, 'booking_url');
  const sourceUrl = pickText(item, 'source_url');
  const mapUrl = pickText(item, 'map_url');

  const latitude = prof.latitude ?? item?.latitude;
  const longitude = prof.longitude ?? item?.longitude;
  const isClickableAddress = !isPopup && latitude != null && longitude != null && typeof ctx?.onShowPinOnMap === 'function';

  const handleAddressClick = (e) => {
    if (isClickableAddress) {
      e.preventDefault();
      ctx.onShowPinOnMap({
        points: [
          {
            ...item,
            category: tag,
            latitude: latitude,
            longitude: longitude,
            time_suggestion: timeSuggestion || '',
            rating_score: ratingScore,
            review_count: reviewCount,
            image_url: item?.image_url || pickText(item, 'photos') || block?.image_url
          }
        ],
        center_latitude: latitude,
        center_longitude: longitude,
        zoom: 15
      });
    }
  };

  const containerClass = isPopup
    ? 'mta-result-card mta-card--popup mta-map-popup-card mta-map-popup-theme'
    : 'mta-result-card';

  return html`
    <div class="${containerClass}">
      <${CardMedia} item=${item} block=${block} ctx=${ctx} variant=${variant} onClose=${onClose} />

      ${tag ? html`<div class="mta-card-tag">${tag}</div>` : ''}
      <h3 class="mta-card-title">${name}</h3>

      <div class="mta-card-meta">
        ${address
          ? html`
              <div
                class=${`mta-card-meta-item ${isClickableAddress ? 'mta-clickable-address' : ''}`}
                onClick=${handleAddressClick}
              >
                <span class="mta-item-pin-icon"><${IconMapPin} /></span>
                <span>${address}</span>
              </div>
            `
          : ''}
        ${timeSuggestion
          ? html`
              <div class="mta-card-meta-item">
                <span style="display:inline-flex; align-items:center; color:var(--mta-text-soft);"><${IconCalendar} /></span>
                <span>${timeSuggestion}</span>
              </div>
            `
          : ''}
        ${ratingScore
          ? html`
              <div class="mta-card-meta-item" title="Rating">
                <span>⭐ ${ratingScore} ${reviewCount ? `(${reviewCount})` : ''}</span>
              </div>
            `
          : ''}
        ${operatingHours
          ? html`
              <div class="mta-card-meta-item" title=${label(ctx, 'operating_hours', 'Opening hours')}>
                <span>🕒 ${operatingHours}</span>
              </div>
            `
          : ''}
        ${phone
          ? html`
              <div class="mta-card-meta-item" title="Phone">
                <span>📞 ${phone}</span>
              </div>
            `
          : ''}
        ${email
          ? html`
              <div class="mta-card-meta-item" title="Email">
                <span>✉️ <a href="mailto:${email}" style="color: inherit;">${email}</a></span>
              </div>
            `
          : ''}
      </div>

      <${CardDescription} description=${description} ctx=${ctx} variant=${variant} />

      ${!isPopup ? html`<${CardChips} items=${amenities} />` : ''}

      <${CardLinks} links=${[
        { href: mapUrl, text: label(ctx, 'show_on_map', 'Show on map') },
        { href: bookingUrl, text: label(ctx, 'book_now', 'Book now') },
        { href: website, text: label(ctx, 'website', 'Website') },
        { href: sourceUrl, text: label(ctx, 'show_on_site', 'Show on site') }
      ]} />
    </div>
  `;
}
